import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import { InputAdornment } from '@mui/material';
import { Search } from '@mui/icons-material';
import type { SupervisorInfo } from './type';
import { DELEGATE_TEXT } from '../../lang';
import { useAddDelegator, useGetStaffInfoByNetID } from '../../../graphql';
import {
  Dialog,
  TextField,
  IconButton,
  type AlertDetails,
  Alert,
  Button,
} from '../../../../../components';

export type AddDelegatorDialogProps = {
  open: boolean;
  currentNetId: string;
  onClose: () => void;
  onAddSuccess: () => void;
  refetchDelegatorList: () => void;
};

export const AddDelegatorDialog: React.FC<AddDelegatorDialogProps> = props => {
  const { open, currentNetId, onClose, refetchDelegatorList, onAddSuccess } =
    props;

  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const initialSupInfo: SupervisorInfo = {
    name: '',
    netId: '',
    email: '',
    department: '',
    delegateRole: DELEGATE_TEXT.DIALOG_TEXT.SUPERVISOR,
  };
  const [supervisorInfo, setSupervisorInfo] =
    useState<SupervisorInfo>(initialSupInfo);
  const emptyInfo = supervisorInfo.netId === '';
  const textFieldRef = useRef<HTMLInputElement>(null);

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const handleSearchAndFill = async (): Promise<void> => {
    const searchId = textFieldRef?.current?.value ?? '';
    if (searchId === null || searchId === '') return;

    try {
      const res = await useGetStaffInfoByNetID(client, searchId);
      if (res !== null) {
        const { department, email, netId, givenName, surname } = res;
        setSupervisorInfo({
          name: `${surname?.toUpperCase()}, ${givenName}`,
          netId,
          email,
          department,
          delegateRole: DELEGATE_TEXT.DIALOG_TEXT.SUPERVISOR,
        });
      } else {
        // no matched,  clear the field
        setSupervisorInfo(initialSupInfo);
        setAlertDetails({
          alertContent: DELEGATE_TEXT.DIALOG_TEXT.NO_MATCHED,
          alertType: 'warning',
        });
        setOpenAlert(true);
      }
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const supervisorInfoFieldMap = [
    {
      label: DELEGATE_TEXT.NAME,
      fullWidth: false,
      value: supervisorInfo.name,
    },
    {
      label: DELEGATE_TEXT.NET_ID,
      fullWidth: false,
      value: supervisorInfo.netId,
    },
    {
      label: DELEGATE_TEXT.EMAIL,
      fullWidth: true,
      value: supervisorInfo.email,
    },
    {
      label: DELEGATE_TEXT.DEPARTMENT,
      fullWidth: false,
      value: supervisorInfo.department,
    },
    {
      label: DELEGATE_TEXT.ROLE,
      fullWidth: false,
      value: supervisorInfo.delegateRole,
    },
  ];

  const handleRefChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (textFieldRef.current) {
      textFieldRef.current.value = event.target.value;
    }
  };

  const addDelegator = useAddDelegator(refetchDelegatorList);
  const handleAddDelegator = async (): Promise<void> => {
    const { netId: delegateNetID } = supervisorInfo;

    // "from" stands for current netId,  "to" stands for the delegator
    const variables = {
      input: {
        from: currentNetId,
        to: delegateNetID,
        role: [DELEGATE_TEXT.DIALOG_TEXT.SUPERVISOR],
      },
    };
    try {
      await addDelegator({
        variables,
      });
      // open alert in parent component and then close the dialog
      onAddSuccess();
      onClose();
    } catch (error: any) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error).message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  return (
    <ContentBox>
      <Dialog
        openDialog={open}
        dialogTitle={DELEGATE_TEXT.ADD_DELEGATOR}
        onDialogClose={onClose}
        width="800px"
      >
        <div className="w-full py-4 px-8">
          <TextField
            className="w-full"
            required
            inputRef={textFieldRef}
            InputLabelProps={{ shrink: true }}
            onChange={handleRefChange}
            placeholder={DELEGATE_TEXT.DIALOG_TEXT.PLACE_HOLDER}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    icon={<Search />}
                    onClick={handleSearchAndFill}
                    color="primary"
                    title={DELEGATE_TEXT.DIALOG_TEXT.PLACE_HOLDER}
                  />
                </InputAdornment>
              ),
            }}
          />
          <div
            className={emptyInfo ? 'invisible' : 'grid grid-cols-2 gap-6 mt-6'}
          >
            {supervisorInfoFieldMap.map((item, index) => (
              <TextField
                className={item.fullWidth ? 'col-span-2' : ''}
                key={index}
                value={item.value}
                disabled={true}
                label={item.label}
                InputLabelProps={{ shrink: true }}
              />
            ))}
          </div>

          <h2 className="mt-12 mb-2 flex justify-center text-[#951D1D]">
            {DELEGATE_TEXT.DIALOG_TEXT.DUPLICATE}
          </h2>
          <div className="w-full flex justify-center">
            <Button
              variant="contained"
              type="submit"
              disabled={emptyInfo}
              onClick={handleAddDelegator}
            >
              {DELEGATE_TEXT.DIALOG_TEXT.ADD}
            </Button>
            <Button className="!ml-4" variant="text" onClick={onClose}>
              {DELEGATE_TEXT.DIALOG_TEXT.CLOSE}
            </Button>
          </div>
        </div>
        <Alert
          openAlert={openAlert}
          setOpenAlert={setOpenAlert}
          alertDetails={alertDetails}
        />
      </Dialog>
    </ContentBox>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
`;
